import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap border font-medium text-base outline-none transition-[background-color,border-color,color,opacity] pointer-coarse:after:absolute pointer-coarse:after:size-full pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-64 sm:text-sm [&_svg:not([class*='text-'])]:text-current [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:-mx-0.5",
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        default: "h-9 px-[calc(--spacing(3)-1px)] sm:h-8",
        icon: "size-9 sm:size-8",
        "icon-lg": "size-10 sm:size-9",
        "icon-sm": "size-8 sm:size-7",
        "icon-xl":
          "size-11 sm:size-10 [&_svg:not([class*='size-'])]:size-5 sm:[&_svg:not([class*='size-'])]:size-4.5",
        "icon-xs":
          "size-7 sm:size-6 not-in-data-[slot=input-group]:[&_svg:not([class*='size-'])]:size-4 sm:not-in-data-[slot=input-group]:[&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 px-[calc(--spacing(3.5)-1px)] sm:h-9",
        sm: "h-8 gap-1.5 px-[calc(--spacing(2.5)-1px)] sm:h-7",
        xl: "h-11 px-[calc(--spacing(4)-1px)] text-lg sm:h-10 sm:text-base [&_svg:not([class*='size-'])]:size-5 sm:[&_svg:not([class*='size-'])]:size-4.5",
        xs: "h-7 gap-1 px-[calc(--spacing(2)-1px)] text-sm sm:h-6 sm:text-xs [&_svg:not([class*='size-'])]:size-4 sm:[&_svg:not([class*='size-'])]:size-3.5",
      },
      variant: {
        default:
          "border-primary bg-primary text-primary-foreground hover:bg-primary/92 [:active,[data-pressed]]:bg-primary/85",
        destructive:
          "border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/92 [:active,[data-pressed]]:bg-destructive/85",
        "destructive-outline":
          "border-okk-danger-line bg-transparent text-okk-danger-ink hover:bg-okk-danger-tint [:active,[data-pressed]]:bg-okk-danger-tint",
        ghost:
          "border-transparent bg-transparent text-foreground hover:bg-accent [:active,[data-pressed]]:bg-accent",
        link: "border-transparent bg-transparent text-foreground underline-offset-4 hover:underline",
        outline:
          "border-input bg-background text-foreground hover:bg-accent [:active,[data-pressed]]:bg-accent",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 [:active,[data-pressed]]:bg-secondary/70",
        paper:
          "border-okk-terracotta-line bg-okk-terracotta-tint text-okk-terracotta-ink hover:bg-okk-terracotta-line/40 [:active,[data-pressed]]:bg-okk-terracotta-line/60",
      },
    },
  },
);

interface ButtonProps extends useRender.ComponentProps<"button"> {
  variant?: VariantProps<typeof buttonVariants>["variant"];
  size?: VariantProps<typeof buttonVariants>["size"];
  isLoading?: boolean;
}

function Button({
  className,
  variant,
  size,
  render,
  isLoading = false,
  children,
  ...props
}: ButtonProps) {
  const typeValue: React.ButtonHTMLAttributes<HTMLButtonElement>["type"] = render
    ? undefined
    : "button";

  const defaultProps = {
    className: cn(buttonVariants({ className, size, variant })),
    "data-slot": "button",
    type: typeValue,
  };

  const mergedProps = mergeProps<"button">(defaultProps, { ...props, children });
  const content = (
    <>
      {mergedProps.children}
      {isLoading ? (
        <span
          aria-hidden="true"
          data-slot="button-loading"
          className="pointer-events-none absolute inset-0 animate-skeleton [--skeleton-base:--alpha(var(--color-white)/14%)] [--skeleton-highlight:--alpha(var(--color-white)/68%)] [background:linear-gradient(120deg,var(--skeleton-base)_38%,var(--skeleton-highlight),var(--skeleton-base)_62%)_transparent_0_0/200%_100%_fixed]"
        />
      ) : null}
    </>
  );

  return useRender({
    defaultTagName: "button",
    props: {
      ...mergedProps,
      "data-loading": isLoading ? "true" : undefined,
      children: content,
    },
    render,
  });
}

export { Button, buttonVariants };
